// JobSpecQAPanel — QA reviewer surface for the bound JobSpec.
// Shows submitted phases (photos + checklist responses grouped by phase)
// and lets the reviewer PASS or FAIL each one.
// FAIL returns the phase to the installer with a required reason.

import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Camera, Clock, AlertTriangle, ShieldCheck } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useAppData } from "@/data/AppDataContext";
import { resolveBoundSpec, formatSpecRef, type BoundJobSpec, type JobSpecPhaseName } from "@/data/jobSpec";
import { qaPassPhase, qaFailPhase } from "@/data/jobSpecExecution";

interface Props {
  jobId: string;
  bound?: BoundJobSpec;
}

const QA_REVIEWER_NAME_STORAGE_KEY = "jobspec_execution_qa_reviewer_name";

function formatPhotoLabel(key: string): string {
  return key
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");
}

export function JobSpecQAPanel({ jobId, bound }: Props) {
  const { specExecutions, setSpecExecutions } = useAppData();
  const [reviewerName, setReviewerName] = useState(() => {
    if (typeof window === "undefined") return "";
    return window.localStorage.getItem(QA_REVIEWER_NAME_STORAGE_KEY) ?? "";
  });
  const [failReasons, setFailReasons] = useState<Record<string, string>>({});

  const spec = useMemo(() => resolveBoundSpec(bound), [bound]);
  const execution = useMemo(
    () => specExecutions.find((e) => e.jobId === jobId),
    [specExecutions, jobId],
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    const next = reviewerName.trim();
    if (!next) {
      window.localStorage.removeItem(QA_REVIEWER_NAME_STORAGE_KEY);
      return;
    }
    window.localStorage.setItem(QA_REVIEWER_NAME_STORAGE_KEY, next);
  }, [reviewerName]);

  if (!bound || !spec) return null;

  if (!execution || execution.phases.every((p) => p.status === "not_started")) {
    return (
      <Card className="shadow-none border-dashed">
        <CardContent className="p-4 text-sm text-muted-foreground">
          No installer activity yet — nothing to review.
        </CardContent>
      </Card>
    );
  }

  const handlePass = (phaseName: JobSpecPhaseName) => {
    if (!reviewerName.trim()) {
      toast.error("Enter your QA reviewer name first.");
      return;
    }
    setSpecExecutions((prev) =>
      prev.map((e) => {
        if (e.jobId !== jobId) return e;
        const r = qaPassPhase(e, phaseName, reviewerName.trim());
        if (r.ok === true) {
          toast.success(`Phase "${phaseName}" passed QA`);
          return r.execution;
        } else {
          toast.error(r.reason);
          return e;
        }
      }),
    );
  };

  const handleFail = (phaseName: JobSpecPhaseName) => {
    if (!reviewerName.trim()) {
      toast.error("Enter your QA reviewer name first.");
      return;
    }
    const reason = failReasons[phaseName]?.trim() ?? "";
    if (!reason) {
      toast.error("Provide a fail reason.");
      return;
    }
    setSpecExecutions((prev) =>
      prev.map((e) => {
        if (e.jobId !== jobId) return e;
        const r = qaFailPhase(e, phaseName, reviewerName.trim(), reason);
        if (r.ok === true) {
          toast.success(`Phase "${phaseName}" returned to installer`);
          setFailReasons((fr) => ({ ...fr, [phaseName]: "" }));
          return r.execution;
        } else {
          toast.error(r.reason);
          return e;
        }
      }),
    );
  };

  return (
    <Card className="shadow-none">
      <CardContent className="p-4 space-y-4">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <p className="text-[9px] font-bold text-muted-foreground uppercase tracking-[0.12em] flex items-center gap-1">
              <ShieldCheck className="h-3 w-3" /> QA Review
            </p>
            <h3 className="text-base font-semibold leading-tight mt-0.5">{spec.label}</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              <code className="font-mono">{formatSpecRef(bound)}</code>
            </p>
          </div>
          <div className="w-48">
            <Input
              value={reviewerName}
              onChange={(e) => setReviewerName(e.target.value)}
              placeholder="QA reviewer name"
              className="h-8 text-xs"
            />
          </div>
        </div>

        {execution.allPhasesComplete && (
          <div className="rounded-md border border-success/30 bg-success/5 p-2 text-xs flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-success" />
            All phases passed QA. Job is QA-complete.
          </div>
        )}

        <div className="space-y-3">
          {spec.phases.map((specPhase) => {
            const phase = execution.phases.find((p) => p.phaseName === specPhase.name)!;
            const reviewable = phase.status === "submitted";

            return (
              <div key={specPhase.name} className="rounded-md border p-3 space-y-3">
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold">{specPhase.label}</span>
                    {phase.status === "qa_passed" && (
                      <Badge className="bg-success text-success-foreground gap-1">
                        <CheckCircle2 className="h-3 w-3" /> Passed
                      </Badge>
                    )}
                    {phase.status === "qa_failed" && (
                      <Badge variant="destructive" className="gap-1">
                        <XCircle className="h-3 w-3" /> Failed
                      </Badge>
                    )}
                    {phase.status === "submitted" && (
                      <Badge variant="outline" className="gap-1">
                        <Clock className="h-3 w-3" /> Awaiting Review
                      </Badge>
                    )}
                    {(phase.status === "not_started" || phase.status === "in_progress") && (
                      <Badge variant="outline">Not Submitted</Badge>
                    )}
                  </div>
                  {phase.completedBy && phase.completedAt && (
                    <span className="text-[10px] text-muted-foreground">
                      Submitted by {phase.completedBy} ·{" "}
                      {new Date(phase.completedAt).toLocaleString()}
                    </span>
                  )}
                </div>

                {/* Submitted Photos */}
                <div className="space-y-1.5">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    Photos
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-1.5">
                    {specPhase.requiredPhotos.map((pt) => {
                      const photo = phase.photos.find((p) => p.photoType === pt);
                      return (
                        <div
                          key={pt}
                          className={`rounded-md border px-2 py-1.5 text-xs flex items-center gap-2 ${
                            photo ? "border-success/40 bg-success/5" : "border-destructive/30 bg-destructive/5"
                          }`}
                        >
                          {photo ? (
                            <CheckCircle2 className="h-3.5 w-3.5 text-success shrink-0" />
                          ) : (
                            <Camera className="h-3.5 w-3.5 text-destructive shrink-0" />
                          )}
                          <span className="truncate">{formatPhotoLabel(pt)}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Checklist responses */}
                <div className="space-y-1">
                  <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                    Checklist
                  </div>
                  <ul className="space-y-0.5">
                    {specPhase.checklistItems.map((item) => {
                      const c = phase.checklist.find((x) => x.itemId === item.id);
                      const ok = !!c?.checked;
                      return (
                        <li key={item.id} className="text-xs flex items-center gap-1.5">
                          {ok ? (
                            <CheckCircle2 className="h-3 w-3 text-success" />
                          ) : (
                            <XCircle className="h-3 w-3 text-destructive" />
                          )}
                          <span className={ok ? "" : "text-muted-foreground"}>{item.label}</span>
                        </li>
                      );
                    })}
                  </ul>
                </div>

                {/* Prior fail reason */}
                {phase.status === "qa_failed" && phase.qaFailReason && (
                  <div className="rounded-md border border-destructive/30 bg-destructive/5 p-2 text-xs">
                    <div className="flex items-center gap-1.5 font-semibold text-destructive">
                      <AlertTriangle className="h-3 w-3" /> Prior Fail Reason
                    </div>
                    <p className="text-foreground/80 mt-0.5">{phase.qaFailReason}</p>
                  </div>
                )}

                {/* Pass reviewed metadata */}
                {phase.status === "qa_passed" && phase.qaReviewedAt && (
                  <p className="text-[10px] text-muted-foreground">
                    Passed by {phase.qaReviewer} · {new Date(phase.qaReviewedAt).toLocaleString()}
                  </p>
                )}

                {/* Reviewer actions */}
                {reviewable && (
                  <div className="space-y-2 pt-1 border-t">
                    <Textarea
                      value={failReasons[specPhase.name] ?? ""}
                      onChange={(e) =>
                        setFailReasons((fr) => ({ ...fr, [specPhase.name]: e.target.value }))
                      }
                      placeholder="Fail reason (required only when failing)…"
                      className="min-h-[56px] text-xs"
                    />
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleFail(specPhase.name)}
                      >
                        <XCircle className="h-3.5 w-3.5 mr-1" /> Fail
                      </Button>
                      <Button size="sm" onClick={() => handlePass(specPhase.name)}>
                        <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Pass
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export default JobSpecQAPanel;
